import { useCallback, useRef, useState } from "react";

import { useAxiosClient } from "@/hooks";
import type {
  CreateStudyRunRequest,
  ImportStudyRequest,
} from "@/types/requests";
import type {
  CreateStudyRunResponse,
  GetJobResponse,
  ImportStudyResponse,
} from "@/types/responses";

import { waveFolders } from "./folders";

/* ── Types ─────────────────────────────────────── */

export type TaskStatus =
  | "created"
  | "creating"
  | "failed"
  | "finished"
  | "queued"
  | "started"
  | "starting";

export interface WaveTask {
  error: string | null;
  /** The state's partisan-feed study rather than its tracker study. */
  feeds: boolean;
  /** Payload for a study still to create; null to run an existing one. */
  request: ImportStudyRequest | null;
  runId: string | null;
  state: string;
  status: TaskStatus;
  studyId: string | null;
  wave: number;
}

/* ── Constants ─────────────────────────────────── */

/* API limits: POST /studies/import 10/min, POST /studies/:id/runs 2/min,
   GET /jobs/:id 30/min. */
const CHECK_DELAY_MS = 60000;
const IMPORT_DELAY_MS = 6500;
export const RUN_DELAY_MS = 31000;

/* Production runs share one AWS Lambda limit of 1,000 concurrent tasks and
   each run fans out up to 100 per step, so more than ~7 at once throttle one
   another (and clients' runs) until they fail. */
export const MAX_IN_FLIGHT = 7;
/* A run stops holding a slot after this long, in case its status can't be
   read. */
const RUN_TIMEOUT_MS = 45 * 60000;
/* Typical run length, for the wave's duration estimate. */
export const RUN_MINUTES = 25;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/* ── Hook ──────────────────────────────────────── */

/**
 * Creates the state studies of a wave, then launches their runs paced to the
 * API rate limits, with at most MAX_IN_FLIGHT of them running at once. Runs
 * execute server-side once started, so the tab only needs to stay open until
 * the last one starts.
 */
const useFieldWave = () => {
  const [running, setRunning] = useState(false);
  const [tasks, setTasks] = useState<WaveTask[]>([]);
  const stopRef = useRef(false);

  const client = useAxiosClient("/studies");
  const folderClient = useAxiosClient("/folders");
  const jobsClient = useAxiosClient("/jobs");

  const start = useCallback(
    async (initial: WaveTask[]) => {
      stopRef.current = false;
      setRunning(true);
      const list = initial.map((t) => ({ ...t }));
      const update = (i: number, patch: Partial<WaveTask>) => {
        list[i] = { ...list[i], ...patch };
        setTasks([...list]);
      };
      setTasks([...list]);

      try {
        // Studies whose folder can't be resolved are created at the root;
        // the tracker files them on its next load.
        const folderFor = list.some((t) => t.request && !t.studyId)
          ? await waveFolders(folderClient).catch(() => null)
          : null;
        const folderOf = async (wave: number): Promise<string | null> =>
          folderFor ? folderFor(wave).catch(() => null) : null;

        // 1. Create the studies (fast).
        for (const [i, task] of list.entries()) {
          if (stopRef.current) return;
          if (!task.request || task.studyId) continue;
          update(i, { status: "creating" });
          try {
            const { data } = await client.post<ImportStudyResponse>("/import", {
              ...task.request,
              folder_id: await folderOf(task.wave),
            } satisfies ImportStudyRequest);
            update(i, { status: "created", studyId: data.study.id });
          } catch (e) {
            update(i, {
              error: e instanceof Error ? e.message : String(e),
              status: "failed",
            });
          }
          await sleep(IMPORT_DELAY_MS);
        }

        // 2. Launch the runs (2 per minute), at most MAX_IN_FLIGHT at a time.
        const launchedAt = new Map<number, number>();
        const inFlight = () =>
          [...launchedAt].filter(
            ([i, at]) =>
              list[i].status === "started" && Date.now() - at < RUN_TIMEOUT_MS
          ).length;
        const checkRuns = () =>
          Promise.all(
            list.map(async (task, i) => {
              if (task.status !== "started" || !task.runId) return;
              try {
                const { data } = await jobsClient.get<GetJobResponse<null>>(
                  `/${task.runId}`
                );
                if (data.job.status === "finished") {
                  update(i, { error: null, status: "finished" });
                } else if (
                  data.job.status === "started" ||
                  data.job.status === "queued"
                ) {
                  if (task.error) update(i, { error: null });
                } else {
                  // Failed, or canceled (aborted), which the type omits.
                  update(i, {
                    error: `Run ${data.job.status}`,
                    status: "failed",
                  });
                }
              } catch (e) {
                // Still counted as running; checked again on the next round.
                update(i, {
                  error: e instanceof Error ? e.message : String(e),
                });
              }
            })
          );

        let launched = 0;
        for (const [i, task] of list.entries()) {
          if (stopRef.current) return;
          if (!task.studyId || task.status === "failed") continue;
          while (inFlight() >= MAX_IN_FLIGHT) {
            await sleep(CHECK_DELAY_MS);
            if (stopRef.current) return;
            await checkRuns();
          }
          if (launched > 0) await sleep(RUN_DELAY_MS);
          if (stopRef.current) return;
          update(i, { status: "starting" });
          try {
            const { data } = await client.post<CreateStudyRunResponse>(
              `/${task.studyId}/runs`,
              { county_grounding: true } satisfies CreateStudyRunRequest
            );
            launchedAt.set(i, Date.now());
            update(i, { runId: data.run_id, status: "started" });
            launched++;
          } catch (e) {
            const message = e instanceof Error ? e.message : String(e);
            update(i, { error: message, status: "failed" });
            // Out of credits or locked: every following launch would fail too.
            if (/credit|balance|insufficient/i.test(message)) {
              stopRef.current = true;
            }
          }
        }

        // 3. Follow the last runs to the end; closing the tab doesn't stop
        // them, it only stops this progress view.
        while (inFlight() > 0) {
          await sleep(CHECK_DELAY_MS);
          if (stopRef.current) return;
          await checkRuns();
        }
      } finally {
        setRunning(false);
      }
    },
    [client, folderClient, jobsClient]
  );

  const stop = useCallback(() => {
    stopRef.current = true;
  }, []);

  const reset = useCallback(() => setTasks([]), []);

  return { reset, running, start, stop, tasks };
};

export default useFieldWave;
